import type { Department } from "../types";

export const departments: Department[] = [
  {
    id: "ent",
    name: "ENT (Ear, Nose & Throat)",
    tagline: "Enhance. Restore. Transform.",
    icon: "Ear",
    shortDescription:
      "Comprehensive care for ear, nose, and throat conditions using advanced diagnostic and surgical techniques.",
    fullDescription:
      "The ENT department at Shifa Medical Centre offers expert diagnosis and treatment for a wide range of ear, nose, and throat disorders. Our team of experienced otolaryngologists uses state-of-the-art endoscopic and microsurgical techniques to treat conditions ranging from chronic sinusitis and hearing loss to complex head and neck surgeries. We are committed to restoring your quality of life through minimally invasive procedures and personalized treatment plans.",
    services: [
      "Sinusitis treatment & endoscopic sinus surgery",
      "Hearing loss evaluation & management",
      "Throat infection treatment",
      "Tonsillectomy & adenoidectomy",
      "Septoplasty & rhinoplasty",
      "Vertigo & balance disorder management",
      "Voice & swallowing disorder treatment",
      "Head & neck tumor surgery",
    ],
    image: "/images/departments/ent.jpg",
  },
  {
    id: "hematology",
    name: "Hematology",
    tagline: "Expert Care for Blood Disorders",
    icon: "Droplets",
    shortDescription:
      "Specialized diagnosis and treatment of blood disorders including anemia, clotting disorders, and blood cancers.",
    fullDescription:
      "The Hematology department provides comprehensive care for patients with blood-related disorders. Our hematologists are skilled in diagnosing and managing conditions such as anemia, leukemia, lymphoma, and clotting abnormalities. With access to advanced laboratory diagnostics and the latest treatment protocols, we deliver targeted therapies that improve patient outcomes. Our multidisciplinary approach ensures each patient receives a tailored treatment plan.",
    services: [
      "Anemia diagnosis & management",
      "Leukemia treatment",
      "Lymphoma care",
      "Blood clotting disorder management",
      "Bone marrow biopsy & aspiration",
      "Blood transfusion services",
      "Thalassemia management",
      "Platelet disorder treatment",
    ],
    image: "/images/departments/hematology.jpg",
  },
  {
    id: "neuroscience",
    name: "Neuroscience",
    tagline: "World-class Neurology & Neurosurgery",
    icon: "Brain",
    shortDescription:
      "Advanced neurological and neurosurgical care for complex brain and nervous system conditions.",
    fullDescription:
      "The Neuroscience department at Shifa Medical Centre combines neurology and neurosurgery to provide world-class care for disorders of the brain, spine, and nervous system. From stroke management and epilepsy treatment to complex brain tumor surgeries, our team of neurologists and neurosurgeons leverages cutting-edge imaging, neuronavigation, and minimally invasive surgical techniques. Our dedicated Neuro ICU ensures critical patients receive round-the-clock specialized monitoring.",
    services: [
      "Stroke diagnosis & emergency treatment",
      "Epilepsy management & surgery",
      "Brain tumor surgery",
      "Spinal cord disorder treatment",
      "Parkinson's disease management",
      "Nerve conduction studies & EMG",
      "Headache & migraine clinic",
      "Neurorehabilitation programs",
    ],
    image: "/images/departments/neuroscience.jpg",
  },
  {
    id: "gastroenterology",
    name: "Gastroenterology",
    tagline: "Digestive Health Made Simple",
    icon: "Apple",
    shortDescription:
      "Expert care for digestive system disorders with advanced endoscopic diagnostic and therapeutic procedures.",
    fullDescription:
      "The Gastroenterology department offers comprehensive care for all disorders of the digestive system including the esophagus, stomach, intestines, liver, pancreas, and gallbladder. Our gastroenterologists use the latest endoscopic and imaging technologies to diagnose and treat conditions such as GERD, peptic ulcers, inflammatory bowel disease, and liver disorders. We emphasize early detection and minimally invasive interventions to ensure faster recovery.",
    services: [
      "GERD diagnosis & treatment",
      "Peptic ulcer management",
      "Inflammatory bowel disease (IBD) care",
      "Upper GI endoscopy",
      "Colonoscopy & polypectomy",
      "Liver disease management",
      "ERCP for bile duct disorders",
      "Hepatitis screening & treatment",
    ],
    image: "/images/departments/gastroenterology.jpg",
  },
  {
    id: "orthopaedics",
    name: "Orthopaedics",
    tagline: "Restoring Movement. Rebuilding Lives.",
    icon: "Bone",
    shortDescription:
      "Complete musculoskeletal care from joint replacements and spine surgery to sports medicine and trauma.",
    fullDescription:
      "The Orthopaedics department at Shifa Medical Centre provides comprehensive care for bones, joints, muscles, and ligaments. Our skilled orthopaedic surgeons perform everything from total joint replacements and complex spine surgeries to minimally invasive arthroscopic procedures. With dedicated sports medicine and trauma services, we help patients of all ages regain mobility and return to active lifestyles. Our rehabilitation team works closely with surgeons to ensure optimal recovery.",
    services: [
      "Total knee & hip replacement",
      "Spine surgery & disc repair",
      "Sports medicine & arthroscopy",
      "Trauma & fracture management",
      "Shoulder & elbow surgery",
      "Hand & wrist surgery",
      "Paediatric orthopaedics",
      "Physiotherapy & rehabilitation",
    ],
    image: "/images/departments/orthopaedics.jpg",
  },
  {
    id: "nephrology",
    name: "Nephrology",
    tagline: "Protecting Your Kidneys. Preserving Your Health.",
    icon: "Bean",
    shortDescription:
      "Specialized kidney care including chronic kidney disease management, dialysis, and transplant support.",
    fullDescription:
      "The Nephrology department is dedicated to the prevention, diagnosis, and treatment of kidney diseases. Our nephrologists manage conditions ranging from early-stage chronic kidney disease to end-stage renal failure requiring dialysis. We offer state-of-the-art hemodialysis facilities with experienced nursing staff and provide comprehensive pre- and post-transplant care. Our goal is to preserve kidney function and improve quality of life for every patient.",
    services: [
      "Chronic kidney disease management",
      "Hemodialysis services",
      "Peritoneal dialysis",
      "Kidney transplant evaluation & support",
      "Hypertension & kidney care",
      "Electrolyte disorder management",
      "Kidney biopsy",
      "Diabetic nephropathy treatment",
    ],
    image: "/images/departments/nephrology.jpg",
  },
  {
    id: "dermatology-cosmetology",
    name: "Dermatology & Cosmetology",
    tagline: "Healthy Skin. Confident You.",
    icon: "Sparkles",
    shortDescription:
      "Clinical dermatology and aesthetic treatments including laser therapy, Botox, and PRP for skin rejuvenation.",
    fullDescription:
      "The Dermatology & Cosmetology department offers a full spectrum of skin care services, from clinical dermatology for medical skin conditions to advanced aesthetic procedures. Our dermatologists treat acne, eczema, psoriasis, fungal infections, and skin cancers with evidence-based protocols. On the cosmetic side, we provide laser treatments, Botox, PRP therapy, chemical peels, and anti-aging solutions. Each treatment plan is customized to achieve natural-looking, lasting results.",
    services: [
      "Clinical dermatology consultations",
      "Acne & scar treatment",
      "Eczema & psoriasis management",
      "Laser skin treatments",
      "Botox & dermal fillers",
      "PRP (Platelet-Rich Plasma) therapy",
      "Chemical peels & microdermabrasion",
      "Skin cancer screening & biopsy",
    ],
    image: "/images/departments/dermatology-cosmetology.jpg",
  },
  {
    id: "cardiology",
    name: "Cardiology",
    tagline: "Your Heart, Our Priority",
    icon: "HeartPulse",
    shortDescription:
      "Comprehensive cardiac care with advanced diagnostics including ECG, ECHO, TMT, and interventional cardiology.",
    fullDescription:
      "The Cardiology department at Shifa Medical Centre provides complete heart care from prevention and diagnosis to advanced interventional procedures. Our cardiologists use state-of-the-art diagnostic tools including ECG, echocardiography, treadmill testing (TMT), Holter monitoring, and coronary angiography. Whether you need routine cardiac screening or complex angioplasty and stenting, our team delivers compassionate, evidence-based care to keep your heart healthy.",
    services: [
      "ECG & Holter monitoring",
      "Echocardiography (ECHO)",
      "Treadmill test (TMT)",
      "Coronary angiography",
      "Angioplasty & stenting",
      "Heart failure management",
      "Pacemaker implantation",
      "Preventive cardiology & risk assessment",
    ],
    image: "/images/departments/cardiology.jpg",
  },
  {
    id: "pulmonology",
    name: "Pulmonology",
    tagline: "Safe, Scientific, Systematic",
    icon: "Wind",
    shortDescription:
      "Expert respiratory care for asthma, COPD, and other lung conditions with advanced pulmonary function testing.",
    fullDescription:
      "The Pulmonology department provides comprehensive care for diseases of the lungs and respiratory system. Our pulmonologists specialize in diagnosing and treating asthma, chronic obstructive pulmonary disease (COPD), interstitial lung disease, sleep apnea, and respiratory infections. With advanced pulmonary function testing, bronchoscopy, and sleep study facilities, we deliver precise diagnoses and effective treatment plans. Our approach is safe, scientific, and systematic.",
    services: [
      "Asthma diagnosis & management",
      "COPD treatment & rehabilitation",
      "Pulmonary function testing (PFT)",
      "Bronchoscopy",
      "Sleep apnea evaluation & CPAP therapy",
      "Tuberculosis management",
      "Interstitial lung disease care",
      "Allergy testing & immunotherapy",
    ],
    image: "/images/departments/pulmonology.jpg",
  },
  {
    id: "oncology",
    name: "Oncology (Onco Care)",
    tagline: "Courage. Care. Cure.",
    icon: "Ribbon",
    shortDescription:
      "Comprehensive cancer care including chemotherapy, immunotherapy, radiation therapy, and bone marrow transplant.",
    fullDescription:
      "The Oncology department at Shifa Medical Centre, branded as Onco Care, offers a full spectrum of cancer treatment services. Our multidisciplinary team of medical oncologists, radiation oncologists, and surgical oncologists collaborate to design individualized treatment plans. Equipped with a state-of-the-art Linear Accelerator (LINAC) for precision radiation therapy, advanced chemotherapy suites, and immunotherapy protocols, we provide patients with access to the latest cancer treatments. Our bone marrow transplant program further strengthens our capacity to treat complex hematological cancers.",
    services: [
      "Chemotherapy",
      "Immunotherapy",
      "Radiation therapy (LINAC)",
      "Bone marrow transplant",
      "Surgical oncology",
      "Cancer screening & early detection",
      "Palliative & supportive care",
      "Tumor board & multidisciplinary review",
    ],
    image: "/images/departments/oncology.jpg",
  },
  {
    id: "obstetrics-gynaecology",
    name: "Obstetrics & Gynaecology",
    tagline: "Nurturing Life. Empowering Women.",
    icon: "Baby",
    shortDescription:
      "Complete women's health care including maternity services, IVF, and advanced laparoscopic gynaecological surgery.",
    fullDescription:
      "The Obstetrics & Gynaecology department provides comprehensive women's health services from adolescence through menopause. Our team of experienced obstetricians and gynaecologists offer expert maternity care with modern labor and delivery suites, high-risk pregnancy management, and neonatal support. We also provide fertility treatments including IVF, advanced laparoscopic and hysteroscopic surgeries, and management of conditions such as PCOS, endometriosis, and fibroids.",
    services: [
      "Maternity & antenatal care",
      "Normal & cesarean delivery",
      "High-risk pregnancy management",
      "IVF & fertility treatments",
      "Laparoscopic gynaecological surgery",
      "Hysteroscopy",
      "PCOS & endometriosis management",
      "Menopause care & HRT",
    ],
    image: "/images/departments/obstetrics-gynaecology.jpg",
  },
  {
    id: "general-surgery",
    name: "General Surgery",
    tagline: "Precision. Safety. Recovery.",
    icon: "Scissors",
    shortDescription:
      "Advanced surgical care including laparoscopic, gastrointestinal, breast and endocrine, and trauma surgery.",
    fullDescription:
      "The General Surgery department at Shifa Medical Centre offers a wide range of surgical services performed by highly skilled surgeons. We specialize in minimally invasive laparoscopic procedures that reduce recovery time and hospital stays. Our expertise spans gastrointestinal surgery, hernia repair, breast and endocrine surgery, and emergency trauma surgery. Every procedure is performed with precision and adherence to the highest safety standards, ensuring the best possible outcomes.",
    services: [
      "Laparoscopic surgery",
      "Gastrointestinal surgery",
      "Hernia repair",
      "Breast surgery",
      "Endocrine surgery (thyroid, parathyroid)",
      "Trauma & emergency surgery",
      "Gallbladder surgery (cholecystectomy)",
      "Appendectomy",
    ],
    image: "/images/departments/general-surgery.jpg",
  },
  {
    id: "plastic-reconstructive-surgery",
    name: "Plastic & Reconstructive Surgery",
    tagline: "Restoring Form. Renewing Confidence.",
    icon: "Paintbrush",
    shortDescription:
      "Cosmetic and reconstructive surgery including burn management, microsurgery, and cleft repair.",
    fullDescription:
      "The Plastic & Reconstructive Surgery department provides both aesthetic and reconstructive surgical solutions. Our plastic surgeons are trained in the latest techniques for cosmetic enhancements, post-trauma reconstruction, burn management, microsurgery, and congenital deformity correction including cleft lip and palate repair. We combine surgical expertise with an artistic eye to help patients achieve natural-looking results while restoring function and form after injury or illness.",
    services: [
      "Cosmetic surgery (facelift, liposuction, rhinoplasty)",
      "Burn management & reconstruction",
      "Microsurgery & flap reconstruction",
      "Cleft lip & palate repair",
      "Hand surgery & reconstruction",
      "Scar revision",
      "Breast reconstruction",
      "Skin grafting",
    ],
    image: "/images/departments/plastic-reconstructive-surgery.jpg",
  },
  {
    id: "dentistry",
    name: "Dentistry",
    tagline: "Smile with Confidence.",
    icon: "SmilePlus",
    shortDescription:
      "Complete dental care from preventive and restorative dentistry to orthodontics, implants, and cosmetic procedures.",
    fullDescription:
      "The Dentistry department at Shifa Medical Centre offers comprehensive oral health services for patients of all ages. Our team of dentists and specialists provides preventive care, restorative treatments, orthodontics, dental implants, and cosmetic procedures using advanced digital imaging and modern dental equipment. Whether you need a routine cleaning, a root canal, or a complete smile makeover, our dental team delivers comfortable, high-quality care.",
    services: [
      "Preventive dentistry & cleanings",
      "Restorative dentistry (fillings, crowns)",
      "Orthodontics (braces & aligners)",
      "Dental implants",
      "Cosmetic dentistry (veneers, whitening)",
      "Root canal treatment",
      "Wisdom tooth extraction",
      "Paediatric dentistry",
    ],
    image: "/images/departments/dentistry.jpg",
  },
  {
    id: "clinical-psychology",
    name: "Clinical Psychology",
    tagline: "Healing Minds. Building Resilience.",
    icon: "HeartHandshake",
    shortDescription:
      "Evidence-based psychological care including CBT, DBT, trauma therapy, and family counseling.",
    fullDescription:
      "The Clinical Psychology department provides professional psychological assessment and therapy services for individuals, couples, and families. Our clinical psychologists use evidence-based approaches including Cognitive Behavioral Therapy (CBT), Dialectical Behavior Therapy (DBT), and trauma-focused therapies to address anxiety, depression, PTSD, relationship difficulties, and behavioral challenges. We create a safe, confidential environment where patients can work toward emotional well-being and personal growth.",
    services: [
      "Cognitive Behavioral Therapy (CBT)",
      "Dialectical Behavior Therapy (DBT)",
      "Trauma & PTSD therapy",
      "Family counseling",
      "Couples therapy",
      "Child & adolescent psychology",
      "Stress & anxiety management",
      "Psychological assessment & testing",
    ],
    image: "/images/departments/clinical-psychology.jpg",
  },
  {
    id: "psychiatry",
    name: "Psychiatry",
    tagline: "Mental Health Matters.",
    icon: "BrainCircuit",
    shortDescription:
      "Comprehensive psychiatric care for mental health conditions including depression, anxiety, and addiction counseling.",
    fullDescription:
      "The Psychiatry department at Shifa Medical Centre provides compassionate, evidence-based mental health care. Our psychiatrists are experienced in diagnosing and treating a wide range of conditions including depression, anxiety disorders, bipolar disorder, schizophrenia, OCD, and substance use disorders. We offer pharmacological management, psychotherapy, and addiction counseling tailored to each patient's needs. Our integrated approach ensures continuity of care and long-term mental wellness.",
    services: [
      "Mental health evaluation & diagnosis",
      "Medication management",
      "Depression & anxiety treatment",
      "Bipolar disorder management",
      "Addiction & substance abuse counseling",
      "OCD treatment",
      "Schizophrenia management",
      "Geriatric psychiatry",
    ],
    image: "/images/departments/psychiatry.jpg",
  },
  {
    id: "critical-care",
    name: "Critical Care",
    tagline: "Every Second Counts.",
    icon: "Siren",
    shortDescription:
      "Advanced intensive care with Medical ICU, Surgical ICU, Neuro ICU, CCU, and ECMO capabilities.",
    fullDescription:
      "The Critical Care department at Shifa Medical Centre is equipped to handle the most complex and life-threatening medical emergencies. Our dedicated ICU facilities include Medical ICU, Surgical ICU, Neuro ICU, and a Coronary Care Unit (CCU), each staffed by trained intensivists and critical care nurses around the clock. With advanced monitoring systems, ventilator support, and ECMO (Extracorporeal Membrane Oxygenation) capability, we provide the highest level of intensive care to critically ill patients.",
    services: [
      "Medical ICU",
      "Surgical ICU",
      "Neuro ICU",
      "Coronary Care Unit (CCU)",
      "ECMO support",
      "Mechanical ventilation",
      "Hemodynamic monitoring",
      "Post-surgical critical care",
    ],
    image: "/images/departments/critical-care.jpg",
  },
  {
    id: "paediatrics",
    name: "Paediatrics",
    tagline: "Caring for Your Little Ones.",
    icon: "Baby",
    shortDescription:
      "Complete child healthcare with NICU, PICU, vaccination programs, and growth monitoring services.",
    fullDescription:
      "The Paediatrics department provides comprehensive healthcare for infants, children, and adolescents. Our paediatricians offer preventive care through vaccination programs, growth and development monitoring, and nutritional counseling. For critically ill newborns and children, our Neonatal ICU (NICU) and Paediatric ICU (PICU) are equipped with advanced life-support systems and staffed by trained neonatologists and paediatric intensivists. We treat every child with the gentleness and expertise they deserve.",
    services: [
      "Neonatal ICU (NICU)",
      "Paediatric ICU (PICU)",
      "Vaccination & immunization programs",
      "Growth & development monitoring",
      "Paediatric emergency care",
      "Newborn screening",
      "Childhood infection management",
      "Adolescent health services",
    ],
    image: "/images/departments/paediatrics.jpg",
  },
  {
    id: "endocrinology",
    name: "Endocrinology",
    tagline: "Balancing Hormones. Restoring Health.",
    icon: "Activity",
    shortDescription:
      "Specialized care for hormonal and metabolic disorders including diabetes, thyroid conditions, PCOS, and osteoporosis.",
    fullDescription:
      "The Endocrinology department at Shifa Medical Centre specializes in the diagnosis and management of hormonal and metabolic disorders. Our endocrinologists provide expert care for diabetes, thyroid diseases, polycystic ovary syndrome (PCOS), osteoporosis, adrenal disorders, and pituitary conditions. With advanced diagnostic tools including DEXA scanning for bone density assessment and continuous glucose monitoring, we deliver personalized treatment plans that help patients achieve hormonal balance and improved quality of life.",
    services: [
      "Diabetes management & insulin therapy",
      "Thyroid disorder treatment",
      "PCOS management",
      "Osteoporosis screening & DEXA scan",
      "Adrenal disorder care",
      "Pituitary disorder management",
      "Metabolic syndrome treatment",
      "Growth hormone disorder evaluation",
    ],
    image: "/images/departments/endocrinology.jpg",
  },
];
